import axios from "../utilities/axios";

const ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
const BASE_URL = `${ENDPOINT}/api/v1/search`;

/**
 * Check if Meilisearch-powered search is enabled via environment variable.
 */
export const isSearchEnabled = () => {
    return process.env.REACT_APP_SEARCH_ENABLED === "true";
};

const stripHtml = (text) => String(text || "").replace(/<[^>]+>/g, "");

const findSuggestionMatch = (searchVal, targetText) => {
    const inputLower = searchVal.toLowerCase();
    const targetLower = targetText.toLowerCase();

    if (
        targetLower.startsWith(inputLower) &&
        targetText.length > searchVal.length
    ) {
        return { type: "prefix", matchLength: searchVal.length };
    }

    const matchedIndex = targetLower.indexOf(inputLower);
    if (
        matchedIndex !== -1 &&
        targetText.length > matchedIndex + searchVal.length
    ) {
        return {
            type: "substr",
            matchStart: matchedIndex,
            matchLength: searchVal.length,
        };
    }

    return null;
};

/** Collect full value and each colon-separated segment (e.g. category + subcategory). */
const collectSuggestionSegments = (text) => {
    const plain = stripHtml(text);
    if (!plain) return [];

    const segments = [];
    const seen = new Set();
    const add = (segment) => {
        const trimmed = segment?.trim();
        if (!trimmed || seen.has(trimmed.toLowerCase())) return;
        seen.add(trimmed.toLowerCase());
        segments.push(trimmed);
    };

    const colonIndex = plain.indexOf(":");
    if (colonIndex !== -1) {
        add(plain.substring(0, colonIndex));
        add(plain.substring(colonIndex + 1));
    } else {
        add(plain);
    }

    return segments;
};

const getColonParts = (text) => {
    const plain = stripHtml(text);
    const colonIndex = plain.indexOf(":");
    if (colonIndex === -1) return null;
    return {
        before: plain.substring(0, colonIndex).trim(),
        after: plain.substring(colonIndex + 1).trim(),
    };
};

const isParentCategorySegment = (candidate, text) => {
    const parts = getColonParts(text);
    if (!parts) return false;
    return parts.before.toLowerCase() === candidate.trim().toLowerCase();
};

const isSubcategorySegment = (candidate, text) => {
    const parts = getColonParts(text);
    if (!parts) return false;
    return parts.after.toLowerCase() === candidate.trim().toLowerCase();
};

/** Titles/collections use spaces; category codes use underscores. */
const isHumanReadableSegment = (segment) => /[\s]/.test(segment);

const isCategorySlugSegment = (segment, labelLookup = {}) => {
    if (!segment || isHumanReadableSegment(segment)) return false;
    if (!segment.includes("_")) return false;
    const lower = segment.toLowerCase();
    return Boolean(labelLookup[segment] || labelLookup[lower]);
};

/** Skip slug fragments (e.g. "leashes") that only exist inside a compound key (e.g. "leashes_harnesses"). */
const shouldSkipPartialSlug = (segment, labelLookup = {}) => {
    if (!segment || segment.includes("_")) return false;

    const lower = segment.toLowerCase();
    if (labelLookup[segment] || labelLookup[lower]) return false;

    return Object.keys(labelLookup).some((key) => {
        if (!key.includes("_") || key.toLowerCase() === lower) return false;
        const keyLower = key.toLowerCase();
        return (
            keyLower.startsWith(`${lower}_`) ||
            keyLower.endsWith(`_${lower}`) ||
            keyLower.includes(`_${lower}_`)
        );
    });
};

/** When the API echoes the typed prefix into the hit (e.g. harHarnesses), use the remainder. */
const normalizeSuggestionSegment = (searchVal, segment) => {
    if (!searchVal || !segment || segment.length <= searchVal.length)
        return segment;

    const segmentLower = segment.toLowerCase();
    const inputLower = searchVal.toLowerCase();
    if (!segmentLower.startsWith(inputLower)) return segment;

    const remainder = segment.slice(searchVal.length);
    if (remainder && findSuggestionMatch(searchVal, remainder)) {
        return remainder;
    }

    return segment;
};

const registerLookupLabel = (lookup, key, label) => {
    if (!key || !label) return;
    lookup[key] = label;
    lookup[key.toLowerCase()] = label;
};

/** Map a matched segment to its human-readable label (preserves casing). */
const resolveSegmentDisplayLabel = (
    segment,
    displayText = "",
    labelLookup = {},
) => {
    if (!segment) return segment;

    const lower = segment.toLowerCase();
    if (labelLookup[segment]) return labelLookup[segment];
    if (labelLookup[lower]) return labelLookup[lower];

    for (const [key, label] of Object.entries(labelLookup)) {
        if (key.toLowerCase() === lower) return label;
    }

    for (const label of new Set(Object.values(labelLookup))) {
        if (label.toLowerCase() === lower) return label;
    }

    for (const displaySegment of collectSuggestionSegments(displayText)) {
        if (displaySegment.toLowerCase() === lower) return displaySegment;
    }

    return segment;
};

/**
 * Resolve compound slugs (e.g. leashes_harnesses) via lookup suffix segments (e.g. harnesses).
 */
const resolveLabelForSlug = (slug, displayText = "", labelLookup = {}) => {
    if (!slug) return slug;

    const direct = resolveSegmentDisplayLabel(slug, displayText, labelLookup);
    if (direct !== slug) return direct;

    if (!slug.includes("_")) return slug;

    const parts = slug.split("_").filter(Boolean);
    for (let start = 1; start < parts.length; start += 1) {
        const suffix = parts.slice(start).join("_");
        const resolved = resolveSegmentDisplayLabel(
            suffix,
            displayText,
            labelLookup,
        );
        if (resolved !== suffix) return resolved;
    }

    return slug;
};

const toDisplaySegment = (segment, isDisplay, displayText, labelLookup) => {
    if (!segment) return segment;
    if (isDisplay || isHumanReadableSegment(segment)) return segment;
    return resolveLabelForSlug(segment, displayText, labelLookup);
};

const getSuggestionAheadSuffix = (
    searchVal,
    rawSegment,
    displaySegment,
    match,
    displayText,
    labelLookup,
) => {
    const labelMatch = findSuggestionMatch(searchVal, displaySegment);

    if (labelMatch) {
        return labelMatch.type === "prefix"
            ? displaySegment.slice(labelMatch.matchLength)
            : displaySegment.slice(
                  labelMatch.matchStart + labelMatch.matchLength,
              );
    }

    // Slug prefix matched a compound code that resolves to a display name (e.g. leas → Harnesses).
    if (rawSegment !== displaySegment && match.type === "prefix") {
        return "";
    }

    if (match.type === "prefix") {
        return rawSegment.slice(match.matchLength);
    }

    return rawSegment.slice(match.matchStart + match.matchLength);
};

const getMatchScore = (
    searchVal,
    rawCandidate,
    displaySegment,
    match,
    labelLookup,
    highlightedText,
    labelMatch,
) => {
    const colonParts =
        getColonParts(highlightedText) || getColonParts(displaySegment);
    const beforeColonIsSlug = colonParts
        ? isCategorySlugSegment(colonParts.before, labelLookup)
        : false;

    let score = 0;
    if (labelMatch) score += 100;
    if (labelMatch?.type === "prefix") score += 80;
    else if (labelMatch?.type === "substr" && labelMatch.matchStart === 0)
        score += 40;
    else if (labelMatch?.type === "substr") score += 15;

    if (match.type === "prefix" && !labelMatch) score += 30;

    if (colonParts) {
        if (isParentCategorySegment(rawCandidate, highlightedText)) {
            if (isHumanReadableSegment(colonParts.before)) {
                score += 120;
            } else if (beforeColonIsSlug) {
                score -= 40;
            }
        }

        if (
            isSubcategorySegment(rawCandidate, highlightedText) &&
            beforeColonIsSlug
        ) {
            score += 80;
            if (match.type === "prefix") score += 100;
        }
    }

    const aheadLength =
        match.type === "prefix"
            ? displaySegment.length - match.matchLength
            : displaySegment.length - (match.matchStart + match.matchLength);

    return { score, aheadLength };
};

const resolveSuggestionMatch = (
    searchVal,
    highlightedText,
    displayText,
    labelLookup = {},
) => {
    const candidates = [];
    const addCandidates = (text, isDisplay) => {
        collectSuggestionSegments(text).forEach((segment) => {
            candidates.push({ segment, isDisplay });
        });
    };

    addCandidates(highlightedText, false);
    if (displayText) {
        addCandidates(displayText, true);
    }

    let bestMatch = null;
    let bestRawSegment = "";
    let bestDisplaySegment = "";
    let bestScore = -1;
    let bestAheadLength = Infinity;

    for (const { segment: candidate, isDisplay } of candidates) {
        if (!isDisplay && shouldSkipPartialSlug(candidate, labelLookup))
            continue;

        const rawSegment = candidate;
        const displaySegment = normalizeSuggestionSegment(
            searchVal,
            toDisplaySegment(candidate, isDisplay, displayText, labelLookup),
        );
        const labelMatch = findSuggestionMatch(searchVal, displaySegment);
        const rawMatch = !isDisplay
            ? findSuggestionMatch(searchVal, rawSegment)
            : null;
        const match = labelMatch || rawMatch;
        if (!match) continue;

        const colonParts =
            getColonParts(highlightedText) || getColonParts(displayText);
        const beforeColonIsSlug = colonParts
            ? isCategorySlugSegment(colonParts.before, labelLookup)
            : false;
        if (
            colonParts &&
            beforeColonIsSlug &&
            labelMatch?.type === "substr" &&
            labelMatch.matchStart > 0 &&
            isHumanReadableSegment(displaySegment)
        ) {
            continue;
        }

        const resolvedDisplay = labelMatch
            ? displaySegment
            : resolveLabelForSlug(rawSegment, displayText, labelLookup);
        const { score, aheadLength } = getMatchScore(
            searchVal,
            rawSegment,
            resolvedDisplay,
            match,
            labelLookup,
            highlightedText,
            labelMatch,
        );

        const isBetter =
            score > bestScore ||
            (score === bestScore &&
                (aheadLength < bestAheadLength ||
                    (aheadLength === bestAheadLength && isDisplay)));

        if (isBetter) {
            bestMatch = match;
            bestRawSegment = rawSegment;
            bestDisplaySegment = resolvedDisplay;
            bestScore = score;
            bestAheadLength = aheadLength;
        }
    }

    if (!bestMatch) return null;

    return {
        match: bestMatch,
        segment: bestDisplaySegment,
        rawSegment: bestRawSegment,
    };
};

/**
 * Build a code-to-label lookup from filter data (categories, tags, brands, etc.).
 * @param {Object} [filterData]
 * @returns {Record<string, string>}
 */
export const buildSuggestionLabelLookup = (filterData) => {
    const lookup = {};
    if (!filterData) return lookup;

    filterData.categories?.forEach((category) => {
        registerLookupLabel(lookup, category?.code, category?.name);
        category?.subCategories?.forEach((subCategory) => {
            registerLookupLabel(lookup, subCategory?.code, subCategory?.name);
            if (category?.code && subCategory?.code) {
                const parentParts = category.code.split("_").filter(Boolean);
                for (let start = 1; start < parentParts.length; start += 1) {
                    const parentSuffix = parentParts.slice(start).join("_");
                    registerLookupLabel(
                        lookup,
                        `${parentSuffix}_${subCategory.code}`,
                        subCategory.name,
                    );
                }
            }
        });
    });

    filterData.tagGroups?.forEach((group) => {
        registerLookupLabel(lookup, group?.code, group?.name);
        group?.tags?.forEach((tag) => {
            registerLookupLabel(lookup, tag?.code, tag?.name);
        });
    });

    filterData.brandValues?.forEach((brandValue) => {
        registerLookupLabel(lookup, brandValue?.code, brandValue?.name);
    });

    filterData.brands?.forEach((brand) => {
        if (brand?.id != null && brand?.name)
            registerLookupLabel(lookup, String(brand.id), brand.name);
        if (brand?.name) registerLookupLabel(lookup, brand.name, brand.name);
    });

    return lookup;
};

/**
 * Convert raw autocomplete values (e.g. dry_food) to display labels (e.g. Dry Food).
 * @param {string} text
 * @param {Record<string, string>} [labelLookup]
 * @returns {string}
 */
export const formatSuggestionDisplayText = (text, labelLookup = {}) => {
    const plain = stripHtml(text);
    if (!plain) return "";

    const colonIndex = plain.indexOf(":");
    if (colonIndex !== -1) {
        const prefix = plain.substring(0, colonIndex).trim();
        const suffixCode = plain.substring(colonIndex + 1).trim();
        const prefixLabel = isHumanReadableSegment(prefix)
            ? prefix
            : resolveLabelForSlug(prefix, "", labelLookup);
        const suffixLabel = isHumanReadableSegment(suffixCode)
            ? suffixCode
            : resolveLabelForSlug(suffixCode, "", labelLookup);
        return `${prefixLabel}: ${suffixLabel}`;
    }

    return isHumanReadableSegment(plain)
        ? plain
        : resolveLabelForSlug(plain, "", labelLookup);
};

/**
 * Ghost suffix shown after the current search input.
 * @param {string} searchVal - Current input value.
 * @param {string} highlightedText - Highlighted field value from autocomplete hit.
 * @param {string} [displayText] - Human-readable suggestion label.
 * @returns {string}
 */
export const getSuggestionAheadText = (
    searchVal,
    highlightedText,
    displayText,
    labelLookup = {},
) => {
    if (!searchVal) return "";

    const resolved = resolveSuggestionMatch(
        searchVal,
        highlightedText,
        displayText,
        labelLookup,
    );
    if (!resolved) return "";

    const { match, segment, rawSegment } = resolved;
    return getSuggestionAheadSuffix(
        searchVal,
        rawSegment,
        segment,
        match,
        displayText,
        labelLookup,
    );
};

/**
 * Value applied when user presses Tab — prefix matches complete the full trimmed
 * suggestion; substring matches complete only the matched word segment.
 * @param {string} searchVal - Current input value.
 * @param {string} highlightedText - Highlighted field value from autocomplete hit.
 * @param {string} [displayText] - Human-readable suggestion label.
 * @param {string} [aheadText] - Ghost suffix from getSuggestionAheadText.
 * @param {Record<string, string>} [labelLookup] - Code-to-label map for display casing.
 * @returns {string}
 */
export const getSuggestionTabValue = (
    searchVal,
    highlightedText,
    displayText,
    aheadText,
    labelLookup = {},
) => {
    if (!searchVal) return "";

    const resolved = resolveSuggestionMatch(
        searchVal,
        highlightedText,
        displayText,
        labelLookup,
    );
    if (!resolved) return searchVal + (aheadText || "");

    const { match, segment } = resolved;
    if (match.type === "prefix") {
        return segment;
    }

    return searchVal + (aheadText || "");
};

/**
 * Full value applied when user presses Tab on a suggestion.
 * @param {string} searchVal - Current input value.
 * @param {string} aheadText - Ghost suffix from getSuggestionAheadText.
 * @param {string} [tabValue] - Precomputed Tab completion from getSuggestionTabValue.
 * @returns {string}
 */
export const getSuggestionCompletedValue = (searchVal, aheadText, tabValue) => {
    if (tabValue) return tabValue;
    if (!searchVal) return "";
    return searchVal + (aheadText || "");
};

/**
 * Search products using Meilisearch backend.
 * @param {string} query - Search query string.
 * @param {Object} options - Search options.
 * @param {string} [options.filters] - Meilisearch filter string.
 * @param {string[]} [options.facets] - Facets to retrieve.
 * @param {string} [options.sort] - Sort expression.
 * @param {number} [options.limit] - Max results to return.
 * @param {number} [options.offset] - Results offset for pagination.
 * @param {import('axios').CancelToken} [options.cancelToken] - Axios cancel token.
 * @returns {Promise<{hits: Array, estimatedTotalHits: number, facetDistribution: Object, processingTimeMs: number}|null>}
 */
export const searchProducts = async (query, options = {}) => {
    if (!isSearchEnabled()) return null;
    try {
        const {
            filters,
            facets,
            sort,
            limit,
            offset,
            cancelToken,
            useFeaturedSort,
            ...loggableOptions
        } = options;
        const params = { q: query };
        if (useFeaturedSort) params.use_featured_sort = true;
        if (filters) params.filters = filters;
        if (facets) params.facets = facets;
        if (sort) params.sort = sort;
        if (limit !== undefined) params.limit = limit;
        if (offset !== undefined) params.offset = offset;

        // Avoid logging cancelToken (circular structure)
        const response = await axios.post(`${BASE_URL}/store/products`, {
            ...params,
            // ...(cancelToken ? { cancelToken } : {}),
        });
        return response.data;
    } catch (error) {
        if (axios.isCancel && axios.isCancel(error)) {
            throw error;
        }
        return null;
    }
};

/**
 * Search brands using brands search backend.
 * @param {string} query - Search query string.
 * @param {Object} options - Search options.
 * @param {Array} [options.filter] - Filter array.
 * @param {string} [options.sort] - Sort expression (e.g. created_at:desc).
 * @param {number} [options.limit] - Max results to return.
 * @param {number} [options.offset] - Results offset for pagination.
 * @param {import('axios').CancelToken} [options.cancelToken] - Axios cancel token.
 * @returns {Promise<Object|null>}
 */
export const searchBrands = async (query, options = {}) => {
    if (!isSearchEnabled()) return null;
    try {
        const {
            filter = [],
            sort,
            useFeaturedSort,
            limit,
            offset,
            cancelToken,
            ...loggableOptions
        } = options;
        const params = { q: query, filter };
        if (sort) params.sort = sort;
        if (useFeaturedSort) params.use_featured_sort = true;
        if (limit !== undefined) params.limit = limit;
        if (offset !== undefined) params.offset = offset;

        const response = await axios.post(`${BASE_URL}/store/brands`, {
            ...params,
            // ...(cancelToken ? { cancelToken } : {}),
        });
        return response.data;
    } catch (error) {
        if (axios.isCancel && axios.isCancel(error)) {
            throw error;
        }
        return null;
    }
};

/**
 * Autocomplete search suggestions.
 * @param {string} query - Partial search query.
 * @param {Object} [options] - Search options.
 * @param {Array} [options.filters] - Product filter array (products autocomplete).
 * @param {Array} [options.filter] - Brand filter array (brands autocomplete).
 * @param {string} [options.sort] - Sort expression.
 * @param {number} [options.limit] - Max suggestions to return.
 * @param {number} [options.offset] - Results offset.
 * @param {import('axios').CancelToken} [options.cancelToken] - Axios cancel token.
 * @returns {Promise<{hits: Array, estimatedTotalHits: number, processingTimeMs: number}|null>}
 */
export const autocomplete = async (query, options = {}) => {
    return null;

    //TODO: When need to show search suggestions enable this code
    // if (!isSearchEnabled()) return null;
    // try {
    //   const { filters, filter, sort, limit = 5, offset, index, cancelToken } = options;
    //   const body = { q: query, limit, index };
    //   if (filter !== undefined) body.filter = filter;
    //   else if (filters && filters.length > 0) body.filters = filters;
    //   if (sort) body.sort = sort;
    //   if (offset !== undefined) body.offset = offset;
    //   const response = await axios.post(`${BASE_URL}/store/autocomplete`, body, {
    //     ...(cancelToken ? { cancelToken } : {}),
    //   });
    //   return response.data;
    // } catch (error) {
    //   if (axios.isCancel && axios.isCancel(error)) {
    //     throw error;
    //   }
    //   console.error('Autocomplete error:', error);
    //   return null;
    // }
};

/**
 * Global search across multiple indices (products, brands, etc.).
 * @param {string} query - Search query string.
 * @param {number} [limit=5] - Max results per index.
 * @returns {Promise<Object|null>}
 */
export const globalSearch = async (query, limit = 5) => {
    if (!isSearchEnabled()) return null;
    try {
        const response = await axios.get(`${BASE_URL}/global`, {
            params: { q: query, limit },
        });
        return response.data;
    } catch (error) {
        console.error("Global search error:", error);
        return null;
    }
};

/**
 * Check search service health.
 * @returns {Promise<boolean>}
 */
export const checkSearchHealth = async () => {
    if (!isSearchEnabled()) return false;
    try {
        const response = await axios.get(`${BASE_URL}/health`);
        return response.data?.status === "available";
    } catch (error) {
        return false;
    }
};
